class VariantSpecTable extends HTMLElement {
  /** @type {Map<string, string[]> | null} */
  #rowsBySku = null;

  /** @type {Map<string, string[][]> | null} */
  #rowsBySpec = null;

  /** @type {HTMLUListElement | null} */
  #displayList = null;

  /** @type {object | null} */
  #lastVariant = null;

  /** @type {string | null} */
  #activeFinish = null;

  /** @type {boolean} */
  #debug = false;

  async connectedCallback() {
    this.#displayList = this.querySelector('[data-spec-list]');
    if (!this.#displayList) return;
    this.#debug = String(this.dataset.debugSpec || '').trim() === 'true';
    this.#setVisible(false);

    const csvUrl = String(this.dataset.specCsvUrl || '').trim();
    if (!csvUrl) return;

    try {
      const response = await fetch(csvUrl, { credentials: 'same-origin' });
      if (!response.ok) return;
      const csvText = await response.text();
      const indexes = this.#buildCsvIndexes(csvText);
      this.#rowsBySku = indexes.bySku;
      this.#rowsBySpec = indexes.bySpec;
      this.#applyCsvToList(this.#displayList);
    } catch (_error) {
      return;
    }

    document.addEventListener('variant:update', this.#handleVariantUpdate);
    document.addEventListener('finish:selected', this.#handleFinishSelected);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this.#handleVariantUpdate);
    document.removeEventListener('finish:selected', this.#handleFinishSelected);
  }

  /** @param {string} value */
  #normalizeKey(value) {
    return String(value)
      .trim()
      .toLowerCase()
      .replace(/\u00d7/g, 'x')
      .replace(/\*/g, 'x')
      .replace(/\s+/g, '');
  }

  /** @param {string} value */
  #normalizeEt(value) {
    return String(value).trim().toLowerCase().replace(/mm/g, '').replace(/\s+/g, '').replace(/et/g, '');
  }

  /** @param {string} text */
  #parseSpecText(text) {
    const parts = String(text)
      .replace(/\u4e28/g, '|')
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length < 3) return null;
    return {
      size: this.#normalizeKey(parts[0]),
      pcd: this.#normalizeKey(parts[1]),
      et: this.#normalizeEt(parts[2]),
    };
  }

  /** @param {string[]} row @param {string} finishKey */
  #rowMatchesFinish(row, finishKey) {
    const color = String(row[11] || '').trim().toLowerCase();
    if (!finishKey) return true;
    if (finishKey.includes('polished')) return color.includes('polished');
    if (finishKey.includes('milled')) return color.includes('milled');
    return color.includes(finishKey);
  }

  /** @param {string[][]} rows @param {string} finishKey */
  #pickRowByFinish(rows, finishKey) {
    if (!rows.length) return null;
    const strict = rows.find((row) => this.#rowMatchesFinish(row, finishKey));
    // If finish text differs from CSV wording, still show same spec row.
    return strict || rows[0] || null;
  }

  /** @param {string} line */
  #parseCsvLine(line) {
    const out = [];
    let value = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          value += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        out.push(value.trim());
        value = '';
      } else {
        value += ch;
      }
    }
    out.push(value.trim());
    return out;
  }

  #buildCsvIndexes(csvText) {
    /** @type {Map<string, string[]>} */
    const bySku = new Map();
    /** @type {Map<string, string[][]>} */
    const bySpec = new Map();
    const lines = String(csvText || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.length < 2) return { bySku, bySpec };

    const header = this.#parseCsvLine(lines[0]).map((v) =>
      v
        .toLowerCase()
        .replace(/^\ufeff/, '')
        .trim()
    );
    const fieldIndex = {
      item: header.indexOf('item'),
      series: header.indexOf('series'),
      style: header.indexOf('style'),
      model: header.indexOf('model'),
      size: header.indexOf('size'),
      pcd: header.indexOf('pcd'),
      et: header.indexOf('et'),
      cb: header.indexOf('cb'),
      backspace: header.indexOf('backspace'),
      lip: header.indexOf('lip size'),
      color: header.indexOf('color'),
      cap: header.indexOf('cap'),
      load: header.indexOf('load'),
    };
    if (Object.values(fieldIndex).some((idx) => idx === -1)) {
      fieldIndex.item = 4;
      fieldIndex.series = 1;
      fieldIndex.style = 2;
      fieldIndex.model = 3;
      fieldIndex.size = 5;
      fieldIndex.pcd = 6;
      fieldIndex.et = 7;
      fieldIndex.cb = 8;
      fieldIndex.backspace = 9;
      fieldIndex.lip = 10;
      fieldIndex.color = 11;
      fieldIndex.cap = 12;
      fieldIndex.load = 13;
    }

    for (let i = 1; i < lines.length; i += 1) {
      const row = this.#parseCsvLine(lines[i]);
      const parts = [
        row[fieldIndex.item] || '',
        '',
        row[fieldIndex.series] || '',
        row[fieldIndex.style] || '',
        row[fieldIndex.model] || '',
        row[fieldIndex.size] || '',
        row[fieldIndex.pcd] || '',
        row[fieldIndex.et] || '',
        row[fieldIndex.cb] || '',
        row[fieldIndex.backspace] || '',
        row[fieldIndex.lip] || '',
        row[fieldIndex.color] || '',
        row[fieldIndex.cap] || '',
        row[fieldIndex.load] || '',
      ];

      const item = String(parts[0] || '').trim();
      const size = this.#normalizeKey(parts[5]);
      const pcd = this.#normalizeKey(parts[6]);
      const et = this.#normalizeEt(parts[7]);
      if (!item || !size || !pcd || !et) continue;

      bySku.set(item.toUpperCase(), parts);
      const specKey = `${size}|${pcd}|${et}`;
      const bucket = bySpec.get(specKey) || [];
      bucket.push(parts);
      bySpec.set(specKey, bucket);
    }

    return { bySku, bySpec };
  }

  /** @param {number} position @param {object} variant */
  #readVariantOption(position, variant) {
    if (position === 1) return String(variant.option1 || '').trim();
    if (position === 2) return String(variant.option2 || '').trim();
    if (position === 3) return String(variant.option3 || '').trim();
    return '';
  }

  /** @param {object} variant */
  #resolveVariantSpec(variant) {
    const finishPos = Number(this.dataset.finishOptionPosition || 0);
    const sizeEtPos = Number(this.dataset.sizeEtOptionPosition || 0);
    const boltPos = Number(this.dataset.boltOptionPosition || 0);

    return {
      finish: this.#readVariantOption(finishPos, variant),
      sizeEt: this.#readVariantOption(sizeEtPos, variant),
      bolt: this.#readVariantOption(boltPos, variant),
      sku: String(variant.sku || '').trim(),
    };
  }

  /** @param {string} sizeEt @param {string} bolt */
  #parseSplitSpec(sizeEt, bolt) {
    const parts = String(sizeEt)
      .replace(/\u4e28/g, '|')
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length < 2) return null;

    return {
      size: this.#normalizeKey(parts[0]),
      pcd: this.#normalizeKey(bolt),
      et: this.#normalizeEt(parts[1]),
    };
  }

  /** @param {HTMLUListElement} list */
  #parseVariantSpec(list) {
    const sizeEt = list.dataset.sizeEt || '';
    const bolt = list.dataset.bolt || '';
    const specText = list.dataset.specText || '';

    if (sizeEt && bolt) {
      const splitSpec = this.#parseSplitSpec(sizeEt, bolt);
      if (splitSpec) return splitSpec;
    }

    return this.#parseSpecText(specText);
  }

  /** @param {HTMLUListElement} list */
  #clearListValues(list) {
    list.querySelectorAll('.variant-spec-table__value').forEach((node) => {
      node.textContent = '';
    });
  }

  /** @param {boolean} matched */
  #setVisible(matched) {
    if (matched) {
      this.removeAttribute('hidden');
    } else {
      this.setAttribute('hidden', '');
    }
  }

  /**
   * CSV pipe columns: Item|No.|Series|style|Model|Size|PCD|ET|...
   * Display order: Item, Series, style, Model, Size, PCD, ET, ...
   * @param {HTMLUListElement} list @param {string[]} row
   */
  #fillListFromRow(list, row) {
    const values = [
      row[0],
      row[2],
      row[3],
      row[4],
      row[5],
      row[6],
      row[7],
      row[8],
      row[9],
      row[10],
      row[12],
      row[13],
    ];

    const valueNodes = list.querySelectorAll('.variant-spec-table__value');
    valueNodes.forEach((node, index) => {
      node.textContent = values[index] || '';
    });
  }

  /** @param {string} finish @param {string} sizeEt @param {string} bolt @param {string} [sku] */
  #lookupCsvRow(finish, sizeEt, bolt, sku = '') {
    if (!this.#rowsBySku || !this.#rowsBySpec) return null;

    const finishKey = String(finish).trim().toLowerCase();
    const normalizedSku = String(sku || '').trim().toUpperCase();
    if (normalizedSku) {
      const skuRow = this.#rowsBySku.get(normalizedSku);
      if (skuRow) return skuRow;
    }

    const spec = this.#parseSplitSpec(sizeEt, bolt);
    if (!spec) return null;

    const rows = this.#rowsBySpec.get(`${spec.size}|${spec.pcd}|${spec.et}`) || [];
    const picked = this.#pickRowByFinish(rows, finishKey);
    if (!picked && this.#debug) {
      // eslint-disable-next-line no-console
      console.debug('[variant-spec-table] no row matched', {
        finish,
        sizeEt,
        bolt,
        sku,
        spec,
        candidateRows: rows.length,
      });
    }
    return picked;
  }

  /** @param {{ finish: string, sizeEt: string, bolt: string, sku?: string }} spec */
  #updateFromSpec(spec) {
    const list = this.#displayList;
    if (!list) return;

    list.dataset.finish = spec.finish;
    list.dataset.sizeEt = spec.sizeEt;
    list.dataset.bolt = spec.bolt;

    const fallbackSku = String(this.dataset.selectedVariantSku || '').trim();
    const row = this.#lookupCsvRow(spec.finish, spec.sizeEt, spec.bolt, spec.sku || fallbackSku);
    if (!row) {
      this.#clearListValues(list);
      list.dataset.csvMatched = 'false';
      this.#setVisible(false);
      return;
    }

    this.#fillListFromRow(list, row);
    list.dataset.csvMatched = 'true';
    this.#setVisible(true);
  }

  /** @param {object} variant */
  #updateFromVariant(variant) {
    const spec = this.#resolveVariantSpec(variant);
    if (this.#activeFinish) {
      spec.finish = this.#activeFinish;
    }

    this.#updateFromSpec(spec);
  }

  /** @param {HTMLUListElement} list */
  #applyCsvToList(list) {
    if (!this.#rowsBySku || !this.#rowsBySpec) return;

    const finish = list.dataset.finish || '';
    let row = this.#lookupCsvRow(finish, list.dataset.sizeEt || '', list.dataset.bolt || '');

    if (!row) {
      const parsed = this.#parseVariantSpec(list);
      const finishKey = finish.trim().toLowerCase();
      if (parsed) {
        const rows = this.#rowsBySpec.get(`${parsed.size}|${parsed.pcd}|${parsed.et}`) || [];
        row = this.#pickRowByFinish(rows, finishKey);
      }
    }

    if (!row) {
      this.#clearListValues(list);
      list.dataset.csvMatched = 'false';
      this.#setVisible(false);
      return;
    }

    this.#fillListFromRow(list, row);
    list.dataset.csvMatched = 'true';
    this.#setVisible(true);
  }

  /** @param {CustomEvent} event */
  #handleFinishSelected = (event) => {
    const finishName = event.detail?.finishName;
    if (!finishName) return;

    this.#activeFinish = finishName;

    if (this.#lastVariant) {
      this.#updateFromVariant(this.#lastVariant);
    } else if (this.#displayList) {
      this.#updateFromSpec({
        finish: finishName,
        sizeEt: this.#displayList.dataset.sizeEt || '',
        bolt: this.#displayList.dataset.bolt || '',
      });
    }
  };

  /** @param {CustomEvent} event */
  #handleVariantUpdate = (event) => {
    const productId = event.detail?.data?.productId;
    if (productId && String(this.dataset.productId) !== String(productId)) return;

    const variant = event.detail?.resource;
    if (!variant?.id) return;

    this.#lastVariant = variant;
    this.dataset.selectedVariantId = String(variant.id);
    this.dataset.selectedVariantSku = String(variant.sku || '').trim();
    this.#updateFromVariant(variant);
  };
}

if (!customElements.get('variant-spec-table')) {
  customElements.define('variant-spec-table', VariantSpecTable);
}
